// Shared Add-a-product modal — the ONE add-product surface for the
// Presentation, Estimate, and Quote (CEO 2026-07-29: "there shouldn't
// be such a big UI transition between the Presentation, Estimate, and Sales
// Order"). Extracted verbatim from the estimate's AddProductModal — the
// CEO-endorsed look — so every surface gets the SAME fixed-overlay modal,
// filter row and list rows.
//
// Surface differences are PROPS, not forks:
//   - showQtyPerAdd    → estimate/SO render the "Qty per add" MOQ field; the
//                        presentation omits it (and its logic is absent).
//   - existingProductIds → presentation ONLY: marks an already-added row so the
//                        rep can see it is on the presentation. As of
//                        2026-08-11 it no longer BLOCKS the add — the same
//                        product may appear twice (one item, two logos), so the
//                        row reads "Add again" and stays clickable.
//   - onAdd            → caller-supplied async add handler. The estimate's
//                        addRow (resolve → margin → POST /orders/:id/lines) and
//                        the presentation's resolve → POST /presentations/:id/
//                        items both flow through here.
//   - customProductSlot → optional footer slot (presentation's "Can't find it?
//                        Add custom product" affordance; estimate omits).
//
// 🔴 THERE IS NO ESP SOURCE HERE ANY MORE (CEO 2026-08-20: "bypass ESP
// altogether. Use only merch'd ingested suppliers via PS or otherwise"). The
// Source dropdown, the `source` state, and <EspSearchPanel> are GONE — not
// hidden behind a flag, because a control that still exists is a control a rep
// can reach, and reaching it would add a product we have decided not to sell.
// merch'd's own catalogue is now the only source, so the modal no longer asks
// which one. If ESP is ever revived, `git show` the removal commit; the server
// routes (GET /esp/search, POST /esp/promote) were deliberately left in place
// because 41 already-promoted products live in production.
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, LayoutGrid, List, Search, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { SupplierSaleNote, type SaleLine } from "./SupplierSaleNote";

export interface SupplierOption {
  id: string;
  name: string;
  productCount: number;
}

/** One merch'd-catalog result row. The caller's onAdd receives this shape. */
export interface AddProductRow {
  id: string;
  name: string;
  brand: string | null;
  /** Catalogue SKU (ATS-10001) — THE SKU. */
  sku?: string | null;
  primaryImageUrl: string | null;
  supplierName: string;
  supplierProductNumber: string | null;
  lowestDecoratedPiecePrice: string | null;
  lowestBlankPiecePrice?: string | null;
  /** 🔴 The cost at the supplier's ENTRY (lowest-quantity) bracket — what a
   *  line should be SEEDED with (2026-08-11). The `lowest*` fields above are
   *  the MINIMUM across every bracket, i.e. the largest-quantity price, and
   *  seeding from those quoted the volume cost on a hundred-piece order.
   *  Optional: products with no bracket grid still fall back to `lowest*`. */
  entryPiecePrice?: string | null;
  /** 🔴 STAFF ONLY — the supplier's price breaks (GET /storefront/products for
   *  staff), shown on every result so reps can pick by price (Stephanie
   *  2026-10-12). Absent for older callers. */
  costBreaks?: CostBreak[];
}

export interface CostBreak {
  quantityMin: number;
  quantityMax: number | null;
  netCost: string | null;
  listPrice: string | null;
  isQur: boolean;
  priceIncludes: string | null;
}

export function AddProductModal({
  currency = "USD",
  onClose,
  onAdd,
  showQtyPerAdd = false,
  existingProductIds,
  customProductSlot,
}: {
  /** Currency for the per-each price label on each result row. */
  currency?: string;
  onClose: () => void;
  /** Resolve → POST the picked merch'd row. Throw to surface a failure banner
   *  (the row won't count toward "added"). `opts.qty` is the current Qty-per-add
   *  value ("" when the field is hidden). */
  onAdd: (row: AddProductRow, opts: { qty: string }) => Promise<void>;
  /** Estimate/SO only — render the "Qty per add" field (blank = supplier MOQ). */
  showQtyPerAdd?: boolean;
  /** Presentation only — marks rows already on the presentation so the button
   *  reads "Add again". INFORMATIONAL as of 2026-08-11: it no longer disables
   *  the row (GM Stephanie needs one item shown with two different logos).
   *  Omit (estimate/SO) and every row simply reads "Add". */
  existingProductIds?: Set<string>;
  /** Optional footer slot (e.g. the presentation's custom-product affordance). */
  customProductSlot?: ReactNode;
}) {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [netMin, setNetMin] = useState("");
  const [netMax, setNetMax] = useState("");
  // Merch'd-catalog results view. List (default) preserves the CEO-endorsed
  // estimate look byte-for-byte; grid is the round-53 deferred rider — a denser
  // card gallery for scanning many products. Single-source here so all three
  // surfaces (presentation / estimate / SO) share the SAME toggle, no forks.
  // Grid first, 5 across, in a wide window — like commonsku (Stephanie
  // 2026-10-14). List view is still one click away.
  const [view, setView] = useState<"list" | "grid">("grid");
  // Captured ONCE, in the render phase before the modal commits + autoFocuses
  // its search box (useState's lazy initializer runs on first render only, so
  // document.activeElement is still the control that opened the modal). Focus
  // returns here on close — see the scroll-lock effect below.
  const [invoker] = useState<HTMLElement | null>(() =>
    typeof document !== "undefined"
      ? (document.activeElement as HTMLElement | null)
      : null,
  );
  // Blank = let the server autofill the supplier's minimum order quantity
  // (CEO 2026-07-21 — "usually 24 / 48 / 72, not 1 piece"). A number the rep
  // types is always honoured; only a blank field defers to the MOQ default.
  const [qty, setQty] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [addedCount, setAddedCount] = useState(0);
  const [pendingId, setPendingId] = useState<string | null>(null);
  // The product whose details + price breaks are open (Stephanie 2026-10-12:
  // "we need to be able to see the item details and pricing before picking").
  const [previewing, setPreviewing] = useState<AddProductRow | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const previewingRef = useRef<AddProductRow | null>(null);
  previewingRef.current = previewing;

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        if (previewingRef.current) setPreviewing(null);
        else onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Body-scroll-lock (a11y rider) — while the shared modal is open the
  // background <body> can't scroll behind the fixed overlay; the previous
  // overflow is restored on close. The now-hidden scrollbar's width is added as
  // body padding-right for the lock's duration so the page behind the overlay
  // doesn't reflow ~15px wider (ux 2026-07-30) — a no-op when there's no
  // classic scrollbar (overlay scrollbars / no overflow → width 0). Focus-return
  // (a11y rider) — on close, focus returns to the control that opened the modal
  // (captured in `invoker` above) when it's still in the document. All live in
  // the SHARED modal so the presentation, estimate, and SO inherit them from one
  // source. There was no scroll-lock/focus-trap precedent in the app — this is
  // the first; kept deliberately minimal (lock + width-compensate + return), not
  // a full focus trap.
  useEffect(() => {
    const { body } = document;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
      if (invoker && document.contains(invoker)) invoker.focus();
    };
  }, [invoker]);

  // 2026-05-28 — commonsku-style filter row shared across the presentation,
  // estimate, and quote. As of 2026-08-20 it is Search · Supplier ·
  // Net min · Net max: the leading Source cell went with ESP, since a
  // one-option dropdown asks a question with only one answer.
  // Supplier options come from /storefront/suppliers.
  const suppliersQ = useQuery({
    queryKey: ["catalog", "add-product", "suppliers"],
    queryFn: () =>
      api<{ suppliers: SupplierOption[] }>("/storefront/suppliers"),
    staleTime: 5 * 60 * 1000,
  });

  const catalogQ = useQuery({
    queryKey: ["catalog", "add-product", debounced, supplierId, netMin, netMax],
    queryFn: () => {
      const params = new URLSearchParams();
      if (debounced.trim()) params.set("search", debounced.trim());
      if (supplierId) params.set("supplier_id", supplierId);
      if (netMin.trim() && Number.isFinite(Number(netMin)))
        params.set("net_min", String(Number(netMin)));
      if (netMax.trim() && Number.isFinite(Number(netMax)))
        params.set("net_max", String(Number(netMax)));
      params.set("limit", "24");
      return api<{ rows: AddProductRow[] }>(
        `/storefront/products?${params.toString()}`,
      );
    },
  });

  // Merch'd list-row Add. Serialize adds (pendingId) BEFORE the async caller
  // handler so a double-click can't fire two POSTs. The caller's onAdd owns the
  // resolve → POST; a throw surfaces the banner and leaves the row un-added
  // (addedCount is only bumped on success).
  async function handleAdd(row: AddProductRow): Promise<void> {
    // 🔴 `existingProductIds` no longer BLOCKS (2026-08-11). A product may
    // legitimately appear twice on a presentation — GM Stephanie needs one item
    // shown with two different logos — so "already added" is now information,
    // not a prohibition. The pendingId guard stays: it stops a double-click
    // firing two POSTs, which is a different problem and still real.
    if (pendingId !== null) return;
    setError(null);
    setPendingId(row.id);
    try {
      await onAdd(row, { qty });
      setAddedCount((c) => c + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't add the product.");
    } finally {
      setPendingId(null);
    }
  }


  const rows = catalogQ.data?.rows ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brown-900/30 p-4 py-10"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[1400px] rounded-xl border border-brown-100 bg-white shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-brown-50 px-5 py-4">
          <div>
            <h3 className="font-display text-lg font-bold text-brown-500">
              Add product
            </h3>
            {addedCount > 0 && (
              <p className="text-xs text-brown-300">
                {addedCount} added — keep going or close when you're done.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-brown-300 hover:text-brown-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3 px-5 py-4">
          {error && (
            <p className="rounded-md border border-rust-100 bg-rust-50 px-3 py-2 text-sm text-rust-500">
              {error}
            </p>
          )}
          {/* Commonsku-style filter row: Search · Supplier · Net min · Net max.
              The Source cell that used to lead this row was removed with ESP
              (CEO 2026-08-20); search reclaims its width. */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-[1fr_180px_100px_100px]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brown-300" />
              <Input
                autoFocus
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  // A new search while one product is open goes back to the
                  // results (Stephanie 2026-10-28: "it doesnt do anything
                  // unless i go back a step").
                  setPreviewing(null);
                }}
                placeholder="Search by name, website part # or vendor SKU…"
                className="pl-9"
              />
            </div>
            {/* Supplier · Net min · Net max — merch'd-catalogue filters, always
                shown now that Other is the only catalogue. */}
            <select
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              aria-label="Supplier"
              className="h-9 rounded-md border border-brown-100 bg-white px-3 text-sm text-brown-500 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200"
            >
              <option value="">All suppliers</option>
              {(suppliersQ.data?.suppliers ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.productCount.toLocaleString()})
                </option>
              ))}
            </select>
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={netMin}
              onChange={(e) => setNetMin(e.target.value)}
              placeholder="Net min"
              aria-label="Net minimum price"
            />
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={netMax}
              onChange={(e) => setNetMax(e.target.value)}
              placeholder="Net max"
              aria-label="Net maximum price"
            />
          </div>

          {/* Estimate/SO only: the quantity each "Add" applies to the new line.
              Blank = autofill each product's supplier minimum order quantity
              (CEO 2026-07-21). Prop-gated so the presentation omits it. */}
          {showQtyPerAdd && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="add-product-qty"
                className="text-xs font-medium uppercase tracking-wide text-brown-400"
              >
                Qty per add
              </label>
              <Input
                id="add-product-qty"
                type="number"
                min={1}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder="Min order qty"
                aria-label="Default quantity per added line — blank uses the supplier minimum order quantity"
                className="w-32"
              />
              <span className="text-xs text-brown-300">
                Blank = supplier minimum
              </span>
            </div>
          )}

          {previewing ? (
            <ProductPreview
              row={previewing}
              currency={currency}
              onBack={() => setPreviewing(null)}
              onAdd={() => void handleAdd(previewing)}
              addLabel={
                pendingId === previewing.id
                  ? "Adding…"
                  : existingProductIds?.has(previewing.id)
                    ? "Add again"
                    : "Add"
              }
              addDisabled={pendingId !== null}
            />
          ) : (
          <div className="space-y-2">
            {/* List/Grid view toggle (round-53 deferred rider). Segmented
                control: the peach-active pill share (bg-peach-400) follows
                the (since retired) production calendar's precedent, but this is a MORE
                ACCESSIBLE, denser variant purpose-built for the modal —
                icon + label buttons, aria-pressed on the active view, and
                compact px-2.5 py-1 text-xs sizing. This is the CANONICAL
                segmented-control spec; the two
                OrderDetailPage toggles were backported to match 2026-07-30,
                and qa-segmented-controls pins the grammar at every site
                (change it here → update the gate + every site). Single-
                source in the SHARED modal, so the presentation, estimate, and
                SO all get the SAME toggle — no per-surface fork. Shown only
                once there are results to arrange. */}
            {!catalogQ.isLoading &&
              !catalogQ.isError &&
              rows.length > 0 && (
                <div className="flex items-center justify-end">
                  <div
                    role="group"
                    aria-label="Results view"
                    className="flex overflow-hidden rounded-md border border-brown-50"
                  >
                    <button
                      type="button"
                      onClick={() => setView("list")}
                      aria-label="List view"
                      aria-pressed={view === "list"}
                      className={cn(
                        "inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold",
                        view === "list"
                          ? "bg-peach-400 text-white"
                          : "text-brown-400 hover:bg-cream-100",
                      )}
                    >
                      <List className="h-3.5 w-3.5" /> List
                    </button>
                    <button
                      type="button"
                      onClick={() => setView("grid")}
                      aria-label="Grid view"
                      aria-pressed={view === "grid"}
                      className={cn(
                        "inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold",
                        view === "grid"
                          ? "bg-peach-400 text-white"
                          : "text-brown-400 hover:bg-cream-100",
                      )}
                    >
                      <LayoutGrid className="h-3.5 w-3.5" /> Grid
                    </button>
                  </div>
                </div>
              )}
            <div className="max-h-[62vh] overflow-y-auto">
              {catalogQ.isLoading && (
                <p className="py-8 text-center text-sm text-brown-300">
                  Searching…
                </p>
              )}
              {catalogQ.isError && (
                <p className="py-8 text-center text-sm text-rust-500">
                  Could not load the catalog.
                </p>
              )}
              {!catalogQ.isLoading &&
                !catalogQ.isError &&
                rows.length === 0 && (
                  <p className="py-8 text-center text-sm text-brown-300">
                    {debounced.trim() || supplierId || netMin || netMax
                      ? "No products match those filters."
                      : "Search or pick a supplier to browse the catalog."}
                  </p>
                )}
              {rows.length > 0 && (
                <div
                  className={cn(
                    view === "grid"
                      ? "grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
                      : "space-y-2",
                  )}
                >
                  {rows.map((row) => {
                    // 🔴 entryPiecePrice FIRST — the SAME precedence
                    // OrderDetailPage uses when it seeds the line's cost.
                    // This badge is what a rep reads while DECIDING what to
                    // add, and it was still showing the cheapest bracket while
                    // the line booked the entry bracket: browse said $0.99,
                    // the document said $1.17. The number you shop by and the
                    // number you get must be the same number.
                    const seedPrice =
                      row.entryPiecePrice ??
                      row.lowestDecoratedPiecePrice ??
                      row.lowestBlankPiecePrice;
                    const already =
                      existingProductIds?.has(row.id) ?? false;
                    // Add control — identical semantics in BOTH views (single
                    // add path): disabled only while another add is pending.
                    // "Add again" appears when the caller passes
                    // existingProductIds and this row is among them.
                    // "Add again" rather than a dead "Added": the rep is told
                    // the product is already on the presentation AND can still
                    // act. Keeping the information while dropping the block is
                    // the whole change.
                    const addButtonLabel =
                      pendingId === row.id
                        ? "Adding…"
                        : already
                          ? "Add again"
                          : "Add";
                    // Vendor in bold so it stands out (Stephanie 2026-10-14).
                    const priceMeta = (
                      <>
                        {/* Website part # → which vendor and their real SKU
                            (Stephanie 2026-10-27). */}
                        {row.sku ? `Website # ${row.sku} · ` : ""}
                        <span className="text-sm font-semibold text-brown-500">{row.supplierName}</span>
                        {row.supplierProductNumber ? ` · Vendor SKU ${row.supplierProductNumber}` : ""}
                        {seedPrice && !(row.costBreaks && row.costBreaks.length > 0)
                          ? ` · ${formatCurrency(Number(seedPrice), currency)}/ea`
                          : ""}
                      </>
                    );
                    if (view === "grid") {
                      return (
                        <div
                          key={row.id}
                          className={cn(
                            "flex flex-col rounded-lg border p-2",
                            // 🔴 NO opacity mute (2026-08-11). This card is
                            // fully clickable now — a repeat add is the whole
                            // point — and `opacity-60` is indistinguishable
                            // from the app's own `disabled:opacity-50`
                            // (ui/button.tsx). A rep scanning the grid read it
                            // as disabled and skipped it, which defeated the
                            // change. The already-added signal moved to the
                            // button label ("Add again") and the rust border.
                            already
                              ? "border-rust-200 bg-rust-50/30"
                              : "border-brown-50",
                          )}
                        >
                          <button
                            type="button"
                            onClick={() => setPreviewing(row)}
                            aria-label={`See details for ${row.name}`}
                            className="text-left"
                          >
                          <div className="mb-2 aspect-square w-full overflow-hidden rounded-md border border-brown-50 bg-cream-50">
                            {row.primaryImageUrl ? (
                              <img
                                src={row.primaryImageUrl}
                                alt={row.name}
                                loading="lazy"
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-[9px] uppercase text-brown-300">
                                No image
                              </div>
                            )}
                          </div>
                          <p className="truncate text-sm font-medium text-brown-500">
                            {row.name}
                          </p>
                          {/* One fact per line so the vendor's SKU is never cut
                              off on a narrow card (Stephanie 2026-10-27). */}
                          <div className="space-y-0.5 text-xs text-brown-300">
                            {row.sku && <p className="truncate">Website # {row.sku}</p>}
                            <p className="truncate text-sm font-semibold text-brown-500">{row.supplierName}</p>
                            {row.supplierProductNumber && (
                              <p className="truncate">Vendor SKU <span className="font-semibold text-brown-500">{row.supplierProductNumber}</span></p>
                            )}
                          </div>
                          <PriceBreaks breaks={row.costBreaks} currency={currency} className="mb-1 mt-1" />
                          <p className="mb-2 text-xs text-brown-400 underline underline-offset-2">
                            Details
                          </p>
                          </button>
                          <Button
                            type="button"
                            variant="outline"
                            className="mt-auto w-full"
                            onClick={() => void handleAdd(row)}
                            disabled={pendingId !== null}
                          >
                            {addButtonLabel}
                          </Button>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={row.id}
                        className={cn(
                          "flex items-center gap-3 rounded-lg border p-2",
                          // Same as the grid card: NO opacity mute on a row
                          // that is fully clickable. See the note there.
                          already
                            ? "border-rust-200 bg-rust-50/30"
                            : "border-brown-50",
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => setPreviewing(row)}
                          aria-label={`See details for ${row.name}`}
                          className="flex min-w-0 flex-1 items-center gap-3 text-left"
                        >
                        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-brown-50 bg-cream-50">
                          {row.primaryImageUrl ? (
                            <img
                              src={row.primaryImageUrl}
                              alt={row.name}
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-[9px] uppercase text-brown-300">
                              No image
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-brown-500">
                            {row.name}
                          </p>
                          <p className="truncate text-xs text-brown-300">
                            {priceMeta}
                          </p>
                          <PriceBreaks breaks={row.costBreaks} currency={currency} className="mt-1" />
                          <p className="mt-0.5 text-xs text-brown-400 underline underline-offset-2">
                            Details
                          </p>
                        </div>
                        </button>
                        {/* Estimate's exact plain-text button (CEO-endorsed
                            look). The `already ? "Added"` branch is inert when
                            existingProductIds is absent (estimate/SO repeat-
                            add), so the estimate renders pixel-identically; the
                            presentation passes the set and its added rows read
                            "Added". */}
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => void handleAdd(row)}
                          disabled={pendingId !== null}
                        >
                          {addButtonLabel}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          )}

          {/* Optional footer slot — e.g. the presentation's "Can't find it? Add
              custom product" affordance. Estimate/SO omit it. */}
          {customProductSlot && (
            <div className="border-t border-peach-300/60 pt-3">
              {customProductSlot}
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-brown-50 px-5 py-3">
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

function formatCurrency(amount: number, currency: string): string {
  if (!Number.isFinite(amount)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

// ─── Product preview (Stephanie 2026-10-12) ─────────────────────────────────
// What a rep checks before adding: photos, description, colours, sizes and the
// supplier's own price breaks. Reads the staff product detail
// (GET /storefront/products/:id → `costBreaks`, staff-only). Prices are the
// supplier's numbers as stored, never invented: a break with no price reads
// "Quote required"; no breaks at all says so.
interface PreviewDetail {
  product: {
    name: string;
    description: string | null;
    sku: string | null;
    imageUrls: string[];
    category: { name: string } | null;
    costBreaks?: CostBreak[];
    /** SanMar colours/sizes on sale now — shown beside the regular cost,
     *  never used as the cost (Stephanie 2026-10-28). */
    saleLines?: SaleLine[];
    offers: Array<{
      vendorName: string;
      isPreferred: boolean;
      description: string | null;
      variants: Array<{ color: string | null; size: string | null }>;
    }>;
  };
}

const SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "2XL", "XXL", "3XL", "XXXL", "4XL", "5XL", "6XL"];

function ProductPreview({
  row,
  currency,
  onBack,
  onAdd,
  addLabel,
  addDisabled,
}: {
  row: AddProductRow;
  currency: string;
  onBack: () => void;
  onAdd: () => void;
  addLabel: string;
  addDisabled: boolean;
}) {
  const q = useQuery({
    queryKey: ["catalog", "add-product", "preview", row.id],
    queryFn: () => api<PreviewDetail>(`/storefront/products/${row.id}`),
    staleTime: 5 * 60 * 1000,
  });
  // Made by hand ("Add a custom product")? Then staff can fix or delete it.
  const customQ = useQuery({
    queryKey: ["catalog", "custom", row.id],
    queryFn: () => api<{ custom: boolean }>(`/catalog/manual-products/${row.id}/custom`),
    staleTime: 5 * 60 * 1000,
  });
  const [imageIdx, setImageIdx] = useState(0);
  const p = q.data?.product;
  const offer = p?.offers.find((o) => o.isPreferred) ?? p?.offers[0];
  const images = p?.imageUrls.length ? p.imageUrls : row.primaryImageUrl ? [row.primaryImageUrl] : [];
  const colours = [...new Set((offer?.variants ?? []).map((v) => v.color).filter((c): c is string => !!c))];
  const sizes = [...new Set((offer?.variants ?? []).map((v) => v.size).filter((x): x is string => !!x))].sort((a, b) => {
    const ia = SIZE_ORDER.indexOf(a.toUpperCase());
    const ib = SIZE_ORDER.indexOf(b.toUpperCase());
    return ia === -1 || ib === -1 ? a.localeCompare(b) : ia - ib;
  });
  const breaks = p?.costBreaks ?? [];
  const hasList = breaks.some((b) => b.listPrice !== null);
  const decorated = breaks.some((b) => b.priceIncludes === "decorated");
  const description = p?.description ?? offer?.description ?? null;
  const money = (v: string | null) => (v === null ? "—" : formatCurrency(Number(v), currency));
  const qtyLabel = (b: { quantityMin: number; quantityMax: number | null }) =>
    b.quantityMax === null ? `${b.quantityMin.toLocaleString()}+` : `${b.quantityMin.toLocaleString()}–${b.quantityMax.toLocaleString()}`;

  return (
    <div className="space-y-4" aria-label={`Details for ${row.name}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-brown-400 hover:text-brown-500">
          <ArrowLeft className="h-4 w-4" /> Back to results
        </button>
        {/* A product someone made by hand can be fixed or deleted (Stephanie
            2026-10-30) — opens its catalogue page in a new tab. */}
        {customQ.data?.custom && (
          <a href={`/admin/catalog/products/${row.id}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-brown-400 underline-offset-2 hover:text-brown-500 hover:underline">
            Edit or delete this custom product
          </a>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-[220px_1fr]">
        <div className="mx-auto w-full max-w-[220px]">
          <div className="aspect-square w-full overflow-hidden rounded-md border border-brown-50 bg-cream-50">
            {images[imageIdx] ? (
              <img src={images[imageIdx]} alt={row.name} className="h-full w-full object-contain" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs uppercase text-brown-300">No image</div>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {images.slice(0, 8).map((u, i) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => setImageIdx(i)}
                  aria-label={`Photo ${i + 1}`}
                  className={cn("h-11 w-11 overflow-hidden rounded border", i === imageIdx ? "border-ink" : "border-brown-50")}
                >
                  <img src={u} alt="" loading="lazy" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="min-w-0 space-y-3">
          <div>
            <p className="text-base font-semibold text-brown-500">{row.name}</p>
            <p className="text-xs text-brown-300">
              {row.sku ? `Website # ${row.sku} · ` : ""}
              <span className="text-sm font-semibold text-brown-500">{row.supplierName}</span>
              {[row.supplierProductNumber ? `Vendor SKU ${row.supplierProductNumber}` : null, p?.category?.name].filter(Boolean).map((t) => ` · ${t}`).join("")}
            </p>
          </div>
          {q.isLoading ? (
            <p className="text-sm text-brown-300">Loading details…</p>
          ) : q.isError ? (
            <p className="text-sm text-rust-500">Couldn't load this product's details. You can still add it.</p>
          ) : (
            <>
              {description && (
                <p className="max-h-40 overflow-y-auto whitespace-pre-line text-sm text-brown-400">{description}</p>
              )}
              {colours.length > 0 && (
                <p className="text-sm text-brown-400">
                  <span className="font-medium text-brown-500">Colors ({colours.length}):</span> {colours.join(", ")}
                </p>
              )}
              {sizes.length > 0 && (
                <p className="text-sm text-brown-400">
                  <span className="font-medium text-brown-500">Sizes:</span> {sizes.join(", ")}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {!q.isLoading && !q.isError && (
        <div>
          <p className="mb-1.5 text-sm font-medium text-brown-500">
            Supplier price breaks{decorated ? " (decorated)" : " (blank, before decoration)"}
          </p>
          {breaks.length === 0 ? (
            <p className="text-sm text-brown-300">No price breaks on file for this product — quote required.</p>
          ) : (
            <table className="w-full max-w-md text-left text-sm">
              <thead className="border-b border-brown-50 text-xs text-brown-300">
                <tr>
                  <th scope="col" className="py-1.5 pr-4 font-medium">Quantity</th>
                  <th scope="col" className="py-1.5 pr-4 font-medium">Our cost each</th>
                  {hasList && <th scope="col" className="py-1.5 font-medium">Supplier list each</th>}
                </tr>
              </thead>
              <tbody>
                {breaks.map((b) => (
                  <tr key={`${b.quantityMin}-${b.quantityMax}`} className="border-b border-brown-50 last:border-0">
                    <td className="py-1.5 pr-4 tabular-nums text-brown-500">{qtyLabel(b)}</td>
                    <td className="py-1.5 pr-4 tabular-nums text-brown-500">
                      {b.isQur || b.netCost === null ? "Quote required" : money(b.netCost)}
                    </td>
                    {hasList && <td className="py-1.5 tabular-nums text-brown-400">{money(b.listPrice)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <SupplierSaleNote lines={p?.saleLines} currency={currency} className="mt-3" />
          <p className="mt-1.5 text-xs text-brown-300">Your sell price is set on the line once it's added.</p>
        </div>
      )}

      <div className="flex justify-end">
        <Button type="button" onClick={onAdd} disabled={addDisabled}>
          {addLabel}
        </Button>
      </div>
    </div>
  );
}

/** Every price break on one line, always visible on a result: "100+ $2.49 ·
 *  250+ $2.19 · 1,000+ Quote". The supplier's own numbers, never invented. */
function PriceBreaks({ breaks, currency, className }: { breaks?: CostBreak[]; currency: string; className?: string }) {
  if (!breaks) return null;
  if (breaks.length === 0) {
    return <p className={cn("text-xs text-brown-300", className)}>No price breaks on file — quote required</p>;
  }
  const decorated = breaks.some((b) => b.priceIncludes === "decorated");
  return (
    <p className={cn("flex flex-wrap gap-x-2.5 gap-y-0.5 text-[13px] tabular-nums text-brown-500", className)}>
      {breaks.map((b) => (
        <span key={`${b.quantityMin}-${b.quantityMax}`} className="whitespace-nowrap">
          <span className="text-brown-300">{b.quantityMin.toLocaleString()}+</span>{" "}
          <span className="font-medium">
            {b.isQur || b.netCost === null ? "Quote" : formatCurrency(Number(b.netCost), currency)}
          </span>
        </span>
      ))}
      {/* "blank cost each" was on every card and said nothing (Stephanie
          2026-10-14). Only the exception is worth a word: a price that
          already includes decoration. */}
      {decorated && <span className="whitespace-nowrap text-xs text-brown-300">decorated cost each</span>}
    </p>
  );
}
